import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, BadgeCheck, Globe, MapPin, Building2, Sparkles, Users, Pin, Share2, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import QrCodeModal from '../components/QrCodeModal';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PublicOrgPage — Shareable, unauthenticated dashboard for verified
 * organizations. Accessed at `/orgs/public/:orgId`.
 * Uses a raw axios (no cookie/auth) so links can be sent to funders.
 */
export default function PublicOrgPage() {
  const { orgId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/organizations/public/${orgId}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [orgId]);

  const share = async () => {
    const url = `${window.location.origin}/orgs/public/${orgId}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: data.organization.name, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Public link copied.');
      }
    } catch (e) { /* silent */ }
  };

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
    </div>
  );
  if (notFound || !data) return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white p-6 text-center">
      <Building2 className="w-10 h-10 text-white/20 mb-3" />
      <h1 className="font-serif text-2xl mb-1">Not published</h1>
      <p className="text-xs text-muted-foreground">This organization hasn't been verified yet or the link is wrong.</p>
      <a href="/" className="mt-4 text-xs uppercase tracking-widest text-emerald-400 hover:underline">← Back to WildQuest</a>
    </div>
  );

  const { organization: org, stats, announcements = [], projects = [] } = data;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#0A1712] to-black text-white" data-testid="public-org-page">
      <div className="max-w-2xl mx-auto p-4 space-y-5 pb-24">
        <header className="flex items-start justify-between pt-2">
          <button onClick={() => nav('/')} data-testid="public-org-back" className="p-2 rounded-full hover:bg-white/5">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button onClick={() => setShowQr(true)} data-testid="public-org-share" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 text-black text-xs font-semibold hover:bg-emerald-400">
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </header>

        {showQr && (
          <QrCodeModal
            url={`${window.location.origin}/orgs/public/${orgId}`}
            title={org.name}
            subtitle="Public organization page"
            onClose={() => setShowQr(false)}
          />
        )}

        <div className="rounded-3xl border border-emerald-500/25 bg-gradient-to-br from-emerald-900/25 to-black/60 p-5">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-black/50 border border-white/10 flex items-center justify-center shrink-0">
              {org.logo_url ? (
                <img src={org.logo_url} alt="" className="w-full h-full rounded-2xl object-cover" />
              ) : (
                <Building2 className="w-7 h-7 text-emerald-300" strokeWidth={1.6} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-emerald-300/80 flex items-center gap-1">
                {org.type.replace('_', ' ')}
                <BadgeCheck className="w-3.5 h-3.5 text-emerald-400" />
              </p>
              <h1 className="font-serif text-2xl mt-0.5" data-testid="public-org-name">{org.name}</h1>
              {org.region_hint && (
                <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3 h-3" /> {org.region_hint}
                </p>
              )}
            </div>
          </div>
          <p className="mt-3 text-sm text-white/85 leading-relaxed">{org.description}</p>
          {org.website && (
            <a href={org.website} target="_blank" rel="noreferrer"
               className="mt-3 inline-flex items-center gap-1 text-[11px] text-emerald-300 hover:underline">
              <Globe className="w-3 h-3" /> {org.website.replace(/^https?:\/\//, '')}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>

        <section data-testid="public-org-stats">
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Impact</p>
          <div className="grid grid-cols-3 gap-2">
            <StatTile label="Observations" value={stats.observation_count} icon={Sparkles} />
            <StatTile label="Species" value={stats.species_count} icon={Building2} />
            <StatTile label="Contributors" value={stats.contributor_count} icon={Users} />
          </div>
          {Object.keys(stats.by_category || {}).length > 0 && (
            <div className="mt-3 rounded-2xl border border-white/5 bg-card p-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Coverage by category</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(stats.by_category).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                  <span key={cat} className="rounded-full bg-black/50 border border-white/5 px-2.5 py-1 text-[10px] font-mono">
                    {cat} <span className="text-emerald-300">{count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>

        {announcements.length > 0 && (
          <section data-testid="public-org-announcements">
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Latest news</p>
            <div className="space-y-2">
              {announcements.map((a) => (
                <motion.div
                  key={a.announcement_id}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className={`rounded-2xl border p-3 ${a.pinned ? 'border-amber-400/40 bg-amber-500/5' : 'border-white/5 bg-card'}`}
                  data-testid={`public-ann-${a.announcement_id}`}
                >
                  <p className="font-serif text-base flex items-center gap-1.5">
                    {a.pinned && <Pin className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                    {a.title}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                    {a.author_name} · {new Date(a.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                  <p className="text-sm text-white/85 mt-2 whitespace-pre-wrap">{a.body}</p>
                  {a.reactions && Object.keys(a.reactions).length > 0 && (
                    <div className="mt-2 flex gap-1.5">
                      {Object.entries(a.reactions).map(([e, users]) => (
                        <span key={e} className="text-xs bg-black/40 border border-white/10 rounded-full px-2 py-0.5">
                          {e} <span className="font-mono text-[10px] text-muted-foreground">{users.length}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section data-testid="public-org-projects">
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Active research</p>
            <div className="space-y-2">
              {projects.map((p) => (
                <div key={p.project_id} className="rounded-2xl border border-white/5 bg-card p-3">
                  <p className="text-sm font-serif">{p.name}</p>
                  {p.description && <p className="text-[11px] text-muted-foreground mt-1">{p.description}</p>}
                  <p className="text-[10px] text-emerald-300/70 mt-1 font-mono">
                    {p.observation_count || 0} observations · {p.member_count || 0} contributors
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        <footer className="pt-4 text-center">
          <a href="/" className="text-[10px] uppercase tracking-[0.25em] text-emerald-400/80 hover:text-emerald-300">
            Powered by WildQuest ↗
          </a>
        </footer>
      </div>
    </div>
  );
}

function StatTile({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-black/40 p-3 text-center">
      <Icon className="w-4 h-4 text-emerald-300 mx-auto mb-1" strokeWidth={1.6} />
      <p className="font-mono text-lg text-white">{value ?? 0}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}
